<template>
  <scout-form
    :is-modified="!!toeic || !!toefl || isSetLanguageSkill"
    label="語学力"
  >
    <div class="boss-layout boss-layout-start boss-layout-wrap boss-layout-gap-large">
      <div>
        TOEIC：
        <input
          :value="toeic"
          class="boss-input"
          style="width: 6em;"
          type="number"
          data-test="toeic"
          max="990"
          min="0"
          @change="$emit('update:toeic', Number($event.target.value) || null)"
          @keypress="onlyNumberInRange($event, 990)"
        >点以上
      </div>
      <div>
        TOEFL：
        <input
          :value="toefl"
          class="boss-input"
          style="width: 6em;"
          type="number"
          data-test="toefl"
          max="677"
          min="0"
          @change="$emit('update:toefl', Number($event.target.value) || null)"
          @keypress="onlyNumberInRange($event, 677)"
        >点以上
      </div>
      <div>
        英語スキル：
        <select
          :value="englishSkill.level"
          class="boss-input-select"
          @change="updateEnglishLevel($event.target.value)"
        >
          <option
            value=""
            data-test="english-none"
          >
            指定なし
          </option>
          <option
            v-for="(option ,i) in languageSkillsMaster"
            :key="i"
            :value="option.id"
            :data-test="`english-${option.id}`"
          >
            {{ option.name }}
          </option>
        </select>
        <span>以上</span>
      </div>
    </div>
    <div
      v-for="(languageSkill,i) in languageSkillsWithoutEnglishForDisp"
      :key="i"
      class="pega-label-indent"
    >
      <span
        v-if="i===0"
        class="indented-label"
        :data-test="`label-${i}`"
      >
        英語以外の語学スキル：
      </span>
      <span
        v-else
        class="white-space-label"
        :data-test="`label-${i}`"
      />
      <select
        :value="languageSkill.id"
        class="boss-input-select"
        :data-test="`language-${i}`"
        @change="updateOtherLanguage( { id:$event.target.value || '', level:languageSkill.level }, i )"
      >
        <option
          value=""
          :data-test="`language-${i}-none`"
        >
          指定なし
        </option>
        <template v-for="(option ,j) in languagesMasterWithoutEnglish">
          <option
            v-if="option.id === Number(languageSkill.id) || !selectedLanguageIds.includes(option.id)"
            :key="`language-${i}-${j}`"
            :value="option.id"
            :data-test="`language-${i}-${option.id}`"
          >
            {{ option.name }}
          </option>
        </template>
      </select>
      <select
        :disabled="!languageSkill.id"
        :value="languageSkill.level"
        class="boss-input-select"
        :data-test="`language-level-${i}`"
        @change="updateOtherLanguage( { id:languageSkill.id, level:$event.target.value }, i )"
      >
        <option
          v-for="(option ,j) in languageSkillsMaster"
          :key="`language-level-${i}-${j}`"
          :value="option.id"
          :data-test="`language-level-${i}-${option.id}`"
        >
          {{ option.name }}
        </option>
      </select>
      <span>以上</span>
    </div>
  </scout-form>
</template>

<script>
import ScoutForm from '@/components/atoms/ScoutForm'
import { mapGetters } from 'vuex'
import eventValidations from '@/lib/eventValidations'

export default {
  components: {
    ScoutForm
  },
  mixins: [eventValidations],
  props: {
    toeic: {
      default: null,
      type: Number
    },
    toefl: {
      default: null,
      type: Number
    },
    languageSkills: {
      default: () => [],
      type: Array
    }
  },
  computed: {
    englishId: () => '10', // 10:英語
    defaultLevel: () => '10', // 10:旅行中に必要な会話ができる
    ...mapGetters([
      'languageSkillsMaster',
      'languagesMaster'
    ]),
    internalLanguageSkills: {
      get () {
        return this.languageSkills
      },
      set (value) {
        this.$emit('update:languageSkills', value)
      }
    },
    selectedLanguageIds () {
      return this.internalLanguageSkills.map(internalLanguageSkill => Number(internalLanguageSkill.id))
    },
    englishSkill () {
      return this.internalLanguageSkills.find(internalLanguageSkill => internalLanguageSkill.id === this.englishId) || { id: this.englishId, level: '' }
    },
    languageSkillsWithoutEnglishForDisp () {
      const languageSkillsWithoutEnglishForDisp = this.internalLanguageSkills.filter(internalLanguageSkill => internalLanguageSkill.id !== this.englishId)
      while (languageSkillsWithoutEnglishForDisp.length < 3) { // 常に3件表示するため、不足分はデフォルト値を補填
        languageSkillsWithoutEnglishForDisp.push({ id: '', level: this.defaultLevel })
      }
      return languageSkillsWithoutEnglishForDisp
    },
    languagesMasterWithoutEnglish () {
      return this.languagesMaster.filter(languageMaster => languageMaster.id !== Number(this.englishId))
    },
    isSetLanguageSkill () {
      return !!this.internalLanguageSkills.filter(languageSkill => languageSkill.id !== '' && languageSkill.level !== '').length
    }
  },
  methods: {
    updateEnglishLevel (level) {
      const targetIndex = this.internalLanguageSkills.findIndex(internalLanguageSkill => internalLanguageSkill.id === this.englishId)
      const englishSkill = { id: this.englishId, level: level || '' }
      if (targetIndex !== -1) {
        // 既に英語スキルが存在する場合は新しいオブジェクトで置き換える
        this.internalLanguageSkills.splice(targetIndex, 1, englishSkill)
      } else {
        // 存在しない場合は末尾に追加する
        this.internalLanguageSkills.splice(0, 0, englishSkill)
      }
      this.update()
    },
    updateOtherLanguage (value, index) {
      this.languageSkillsWithoutEnglishForDisp.splice(index, 1, value)
      this.update()
    },
    update () {
      const updateLanguageSkills = [
        this.englishSkill,
        ...this.languageSkillsWithoutEnglishForDisp
      ]
      this.$set(this, 'internalLanguageSkills', updateLanguageSkills)
    }
  }
}
</script>
